import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { filter, isEmpty, map } from 'ramda'
import { Form, useFormik, FormikProvider } from 'formik'
import { Typography, RadioGroup, FormControlLabel } from '@material-ui/core'

import Radios from 'app/componentV4/Radios'
import CheckBox from 'app/componentV4/CheckBox'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'

import { SECONDARY } from 'app/constants/input'
import {
  TYPE_ALL,
  TYPE_STATUS,
  TYPE_SELECTED,
} from 'app/constants/subscriberCRM'
import { useAction } from './useAction'

const ExportCSV = (props) => {
  const { selectedIds = [], totalSelectedCount = 0 } = props
  const { formatMessage } = useIntl()

  const [selected, setSelected] = useState([])
  const [isDisable, setIsDisable] = useState(false)
  const [flatIds, setFlatIds] = useState(selectedIds)

  const {
    state: { status, isLoadingExport },
    exportSubscribers,
    countSubscribersStatus,
  } = useAction(formatMessage)

  const [isOpen, setIsOpen] = useState(false)

  const formik = useFormik({
    initialValues: {
      type: TYPE_SELECTED,
    },
  })

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = useCallback(() => {
    formik.resetForm()
    setIsOpen(false)
  }, [formik])

  const handleSelectStatus = (checked, object) => {
    setSelected((prev) => {
      let state = []
      if (checked) state = [...prev, object]
      else state = filter((list) => list.id !== object.id)(prev)

      state.sort((a, b) => a.id - b.id)
      return state
    })
  }

  const handleExport = useCallback(async () => {
    await exportSubscribers(
      formik.values.type,
      flatIds,
      map((list) => list.value)(selected),
    )
    handleClose()
  }, [exportSubscribers, formik.values.type, flatIds, selected, handleClose])

  const handleRadioChange = useCallback(
    async (value) => {
      if (value !== TYPE_STATUS && selected.length > 0) setSelected([])
      formik.setFieldValue('type', value)
    },
    [formik, selected.length],
  )

  const fetchcountSubscribersStatus = useCallback(() => {
    countSubscribersStatus()
  }, [countSubscribersStatus])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchcountSubscribersStatus, [])

  useEffect(() => {
    setFlatIds(selectedIds)
  }, [selectedIds])

  useEffect(() => {
    if (selected.length > 0 && formik.values.type !== TYPE_STATUS) {
      formik.setFieldValue('type', TYPE_STATUS)
    }
  }, [formik, selected])

  useEffect(() => {
    setIsDisable(
      (formik.values.type === TYPE_STATUS && isEmpty(selected)) ||
        isLoadingExport,
    )
  }, [formik, isLoadingExport, selected])

  return (
    <div>
      <Typography
        className="text-14 text-primary font-500 cursor-pointer whitespace-nowrap"
        onClick={() => handleOpen()}
      >
        {formatMessage({
          id: 'teamMembers.table.action.exportCSV.label',
        })}
      </Typography>

      <SubmitDialog
        id="dialog-export-csv-team-member"
        open={isOpen}
        type={SECONDARY}
        title={formatMessage({
          id: 'exportCSV.title',
        })}
        submitText={formatMessage({
          id: 'action.export',
        })}
        onSubmit={async () => {
          await handleExport()
        }}
        closeText={formatMessage({
          id: 'text.close',
        })}
        onClose={() => handleClose()}
        disabledSubmit={isDisable}
        isLoading={isLoadingExport}
      >
        <div className="bg-primary-200 rounded-8">
          <FormikProvider value={formik}>
            <Form>
              <RadioGroup
                className="m-16 p-10"
                aria-label="export type"
                value={formik.values.type}
                onChange={(event) => {
                  handleRadioChange(event.target.value)
                }}
              >
                <FormControlLabel
                  value={TYPE_ALL}
                  control={
                    <Radios
                      checked={formik.values.type === TYPE_ALL}
                      color={SECONDARY}
                    />
                  }
                  label={
                    <Typography className="text-primary tetx-14">
                      {formatMessage({ id: 'statuse.all' })}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value={TYPE_SELECTED}
                  control={
                    <Radios
                      checked={formik.values.type === TYPE_SELECTED}
                      color={SECONDARY}
                    />
                  }
                  label={
                    <Typography className="text-primary tetx-14">
                      {`${formatMessage({ id: 'selected.label' })} (${flatIds.length})`}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value={TYPE_STATUS}
                  control={
                    <Radios
                      checked={formik.values.type === TYPE_STATUS}
                      color={SECONDARY}
                    />
                  }
                  label={
                    <Typography className="text-primary tetx-14">
                      {formatMessage({ id: 'groups.by.status.label' })}
                    </Typography>
                  }
                />
                <div className="mx-5 md:w-256">
                  {status.map((list, index) => {
                    return (
                      <div
                        key={index}
                        className="text-primary text-14 font-normal text-pretty"
                      >
                        <FormControlLabel
                          className="mr-0"
                          control={
                            <CheckBox
                              checked={selected.some(
                                ({ id }) => id === list.id,
                              )}
                              onChange={(e) => {
                                handleSelectStatus(e.target.checked, list)
                              }}
                              name={`${list.name}-checkbox`}
                              disabled={list.count <= 0}
                            />
                          }
                          label={`${formatMessage(
                            {
                              id: list.label,
                            },
                            list.valueLabel,
                          )} (${list.count})`}
                        />
                      </div>
                    )
                  })}
                </div>
              </RadioGroup>
            </Form>
          </FormikProvider>
        </div>
      </SubmitDialog>
    </div>
  )
}

ExportCSV.propTypes = {
  selectedIds: PropTypes.array,
  totalSelectedCount: PropTypes.number,
}

export default ExportCSV
